import { createAsyncThunk } from '@/redux/createAsyncThunk';
import {
    postAddBidderAddress,
    postAddBidderAddressV2,
    postUpdateBidderAddress,
    postUpdateBidderAddressV2,
    postUpdateProfile,
    UpdateProfileRequest,
} from '@/redux/modules/account/updateAccount/updateAccount.api';
import {
    UPDATE_ACCOUNT_FAIL,
    UPDATE_ACCOUNT_REQUEST,
    UPDATE_ACCOUNT_SUCCESS,
    UPDATE_ACCOUNT_SUGGESTION,
    UPDATE_ACCOUNT_TOAST_MESSAGE,
    UpdateAccountSuggestionAction,
    UpdateBidderAddressV2Params,
    AddBidderAddressV2Params,
} from '@/redux/modules/account/updateAccount/updateAccount.types';
import { fetchBidderDataIfNeeded, storeSavedAddress } from '@/redux/modules/account/user/user.actions';
import { getAuthToken } from '@/redux/modules/account/user/user.selectors';
import { dismissModal, openAddressVerifyModal } from '@/redux/modules/modal';
import { AppDispatch, AppGetState } from '@/redux/store';
import { BidderAddress } from '@/types/BidderAddresses';

import { getDeployment } from '../../config';

// Clears any cached address suggestion/original from a prior address flow so it can't be
// mistakenly reused (and cause duplicate addresses) by a later, unrelated address submission.
export const clearAddressSuggestion = createAsyncThunk<void, void>(
    'la/ui/updateAccount/clearAddressSuggestion',
    () => {}
);

export const updateAccountDetails =
    (updatedProfile: UpdateProfileRequest) => async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postUpdateProfile({
                authToken,
                deployment,
                profile: updatedProfile,
            });

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                // eslint-disable-next-line no-undef
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            return dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

export const updateBidderAddress =
    (bidderID: number, address: BidderAddress, toastMessage: string, verify = false, shouldOpenModal = true) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            const response = await postUpdateBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });
            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });
                if (shouldOpenModal) {
                    dispatch(
                        openAddressVerifyModal({
                            suggestedAddress: response.payload?.suggestedAddress,
                            userEnteredAddress: address,
                        })
                    );
                }
                return;
            }

            dispatch(clearAddressSuggestion());
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            if (toastMessage) {
                dispatch({
                    payload: toastMessage,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
                dispatch({
                    payload: 0,
                    type: UPDATE_ACCOUNT_TOAST_MESSAGE,
                });
            }
            return dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };

type AddressSuggestionResponse = Awaited<ReturnType<typeof postAddBidderAddressV2>>;

const getSuggestionAndSuccessActions = (response: AddressSuggestionResponse, address: BidderAddress) => {
    const mostRecentAddress = response.payload.bidderAddresses.reduce((currentAddress, nextAddress) => {
        if (!currentAddress.addressId) {
            return nextAddress;
        }
        if (!nextAddress.addressId) {
            return currentAddress;
        }
        if (currentAddress.addressId > nextAddress.addressId) {
            return currentAddress;
        }
        return nextAddress;
    });
    const suggestionAction: UpdateAccountSuggestionAction = {
        error: false,
        meta: { actionTime: Date.now() },
        payload: {
            ...response,
            payload: {
                ...response.payload,
                originalAddress: {
                    ...address,
                    addressId: mostRecentAddress.addressId,
                },
            },
        },
        type: UPDATE_ACCOUNT_SUGGESTION,
    };
    return {
        successAction: {
            meta: { actionTime: Date.now() },
            payload: response,
            type: UPDATE_ACCOUNT_SUCCESS,
        },
        suggestionAction,
    };
};

export const updateBidderAddressV2 = createAsyncThunk<void, UpdateBidderAddressV2Params>(
    'la/ui/updateAccount/updateBidderAddressV2',
    async ({ address, bidderId, verify = false }, { dispatch, getState }) => {
        try {
            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            const response = await postUpdateBidderAddressV2({
                address,
                authToken,
                bidderID: bidderId,
                deployment,
                verify,
            });

            const suggestionProvided = response.payload.suggestionProvided;

            if (suggestionProvided) {
                const { successAction, suggestionAction } = getSuggestionAndSuccessActions(response, address);
                dispatch(suggestionAction);
                dispatch(successAction);
            } else {
                dispatch(clearAddressSuggestion());
            }

            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });
            await dispatch(fetchBidderDataIfNeeded({ force: true }));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    }
);

export const addBidderAddressV2 = createAsyncThunk<void, AddBidderAddressV2Params>(
    'la/ui/updateAccount/addBidderAddressV2',
    async ({ address, bidderId, closeAfterVerification = false, verify = false }, { dispatch, getState }) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postAddBidderAddressV2({
                address,
                authToken,
                bidderId,
                deployment,
                verify,
            });
            const suggestionProvided = response.payload.suggestionProvided;

            if (suggestionProvided) {
                const { successAction, suggestionAction } = getSuggestionAndSuccessActions(response, address);
                dispatch(suggestionAction);
                dispatch(successAction);
            } else {
                dispatch(clearAddressSuggestion());
                if (closeAfterVerification) {
                    dispatch(dismissModal());
                }
            }

            await dispatch(fetchBidderDataIfNeeded({ force: true }));
            const savedAddress =
                response.payload?.bidderAddresses?.length > 0 ? response.payload.bidderAddresses[0] : null;
            dispatch(storeSavedAddress(savedAddress));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    }
);

export const addBidderAddress =
    (bidderID: number, address: BidderAddress, verify = false, shouldOpenModal = true) =>
    async (dispatch: AppDispatch, getState: AppGetState) => {
        try {
            const state = getState();
            const authToken = getAuthToken(state);
            const deployment = getDeployment(state);

            dispatch({
                type: UPDATE_ACCOUNT_REQUEST,
            });

            const response = await postAddBidderAddress({
                address,
                authToken,
                bidderID,
                deployment,
                verify,
            });
            if (!Array.isArray(response.payload)) {
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: response,
                    type: UPDATE_ACCOUNT_SUGGESTION,
                });

                if (shouldOpenModal) {
                    dispatch(
                        openAddressVerifyModal({
                            suggestedAddress: response.payload.suggestedAddress,
                            userEnteredAddress: address,
                        })
                    );
                }
                return;
            }
            dispatch(clearAddressSuggestion());
            dispatch({
                meta: { actionTime: Date.now() },
                payload: response,
                type: UPDATE_ACCOUNT_SUCCESS,
            });

            await dispatch(fetchBidderDataIfNeeded({ force: true }));
            const savedAddress = response.payload.length > 0 ? response.payload[0] : null;
            dispatch(storeSavedAddress(savedAddress));
        } catch (error) {
            dispatch({
                error: true,
                payload: error,
                type: UPDATE_ACCOUNT_FAIL,
            });
        }
    };
